import { ReactElement } from "react";
import {
  Routes,
  Route,
  useNavigate,
  useLocation,
  Navigate,
  Outlet,
} from "react-router-dom";

import {
  CONTRACTORS_PATH,
  EMPLOYEES_EOR_PATH,
  EOR_PROMO_PATH,
  OVERVIEW_PATH,
} from "./routes";
import { Header } from "./components/Header/Header";
import styles from "./InternationalSolutionsPage.module.css";
import { NotFoundError, Tab, TabList } from "@justworkshr/milo-core";
import { TABS } from "./constants";
import Overview from "./pages/Overview/Overview";
import Contractors from "./pages/Contractors/Contractors";
import EmployerOfRecord from "./pages/EmployerOfRecord/EmployerOfRecord";
import EORPromo from "./pages/EORPromo/EORPromo";
import { INTERNATIONAL_SOLUTIONS_PATH } from "pages";
import { useGetFeatureFlag } from "lib/launch-darkly";
import {
  useGetIsEmployerOfRecordEnabledQuery,
  useGetIsViaCustomerQuery,
} from "types/generated/operations";
import { FLAG_NAMES, flagIsEnabled, getEnvironmentName } from "lib/environment";
import { LoadingSpinner } from "pages/form-8973/components/LoadingSpinner/LoadingSpinner";

const InternationalSolutionsPage = (): ReactElement => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { getFeatureFlag } = useGetFeatureFlag();
  const { data: eorEnabledData, loading: isEorLoading } =
    useGetIsEmployerOfRecordEnabledQuery();
  const isEmployerOfRecordEnabled =
    eorEnabledData?.authenticatedMember.company.companySetting?.value ===
      "true" && flagIsEnabled(FLAG_NAMES.EMPLOYER_OF_RECORD);
  const { data: viaCustomerData, loading: isViaCustomerLoading } =
    useGetIsViaCustomerQuery();
  const isViaCustomer =
    viaCustomerData?.authenticatedMember.company.companySetting?.value ===
    "true";
  const environment = getEnvironmentName();
  const isSandboxOrStaging =
    environment === "sandbox" || environment === "staging";
  const isEorPromoEnabled =
    getFeatureFlag("pdp-eor-promo-enabled") &&
    !isEmployerOfRecordEnabled &&
    !isSandboxOrStaging &&
    !isViaCustomer;

  const getSelectedTabIndex = (): number => {
    const index = TABS.findIndex((tab) => pathname.includes(tab.path));
    return index !== -1 ? index : 0;
  };

  const handleTabChange = (index: number) => {
    if (index === 0) {
      return navigate(`${INTERNATIONAL_SOLUTIONS_PATH}${OVERVIEW_PATH}`);
    } else if (index === 1) {
      return navigate(`${INTERNATIONAL_SOLUTIONS_PATH}${EMPLOYEES_EOR_PATH}`);
    } else if (index === 2) {
      return navigate(`${INTERNATIONAL_SOLUTIONS_PATH}${CONTRACTORS_PATH}`);
    } else if (index === 3) {
      return navigate(`${INTERNATIONAL_SOLUTIONS_PATH}${EOR_PROMO_PATH}`);
    } else {
      return navigate(`${INTERNATIONAL_SOLUTIONS_PATH}${OVERVIEW_PATH}`);
    }
  };

  if (isEorLoading || isViaCustomerLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className={styles.contentWrapper}>
      <Header />
      <TabList
        orientation="horizontal"
        selected={getSelectedTabIndex()}
        onChange={handleTabChange}
      >
        {TABS.filter((tab) =>
          tab.path === EOR_PROMO_PATH ? isEorPromoEnabled : true
        ).map((tab) => (
          <Tab key={tab.path}>{tab.name}</Tab>
        ))}
      </TabList>
      <div className={styles.componentWrapper}>
        <Outlet />
      </div>

      <Routes>
        <Route path={`${OVERVIEW_PATH}`} element={<Overview />} />
        <Route path={`${EMPLOYEES_EOR_PATH}`} element={<EmployerOfRecord />} />
        <Route path={`${CONTRACTORS_PATH}`} element={<Contractors />} />
        <Route
          path={`${EOR_PROMO_PATH}`}
          element={isEorPromoEnabled ? <EORPromo /> : <NotFoundError />}
        />
        <Route
          path="/"
          element={<Navigate to={`.${OVERVIEW_PATH}`} replace />}
        />
      </Routes>
    </div>
  );
};

export default InternationalSolutionsPage;
